import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Session,
  getActiveSessions,
  terminateSession,
  logoutAllOtherDevices,
} from '../api/authApi';

type PageState = 'loading' | 'loaded' | 'error';

interface ConfirmDialogState {
  isOpen: boolean;
  type: 'single' | 'all';
  sessionId?: string;
  sessionName?: string;
}

// Helper function to format relative time
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  } else {
    return date.toLocaleDateString();
  }
}

// Device icon component based on device type
function DeviceIcon({ deviceType }: { deviceType: string }) {
  const type = deviceType.toLowerCase();

  if (type === 'mobile' || type === 'smartphone') {
    return (
      <svg
        className="w-8 h-8 text-gray-600 dark:text-slate-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
      </svg>
    );
  }

  if (type === 'tablet') {
    return (
      <svg
        className="w-8 h-8 text-gray-600 dark:text-slate-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
        />
      </svg>
    );
  }

  // Default: desktop
  return (
    <svg
      className="w-8 h-8 text-gray-600"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

// Confirmation dialog component
function ConfirmDialog({
  isOpen,
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
  isLoading,
}: {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onCancel}
          aria-hidden="true"
        />

        {/* Dialog */}
        <div className="relative bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-2">{title}</h3>
          <p className="text-gray-600 dark:text-slate-400 mb-6">{message}</p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 dark:text-slate-300 border border-gray-300 dark:border-slate-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
            >
              {isLoading ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Processing...
                </>
              ) : (
                confirmLabel
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Session card component
function SessionCard({
  session,
  onTerminate,
}: {
  session: Session;
  onTerminate: (sessionId: string, sessionName: string) => void;
}) {
  return (
    <div
      className={`bg-white dark:bg-slate-800 rounded-lg shadow p-4 border ${
        session.isCurrent ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200 dark:border-slate-700'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Device Icon */}
        <div className="flex-shrink-0 p-2 bg-gray-100 dark:bg-slate-700 rounded-lg">
          <DeviceIcon deviceType={session.deviceType} />
        </div>

        {/* Session Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-gray-900 dark:text-slate-100 truncate">{session.deviceName}</h3>
            {session.isCurrent && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                Current Session
              </span>
            )}
          </div>

          <p className="text-sm text-gray-600 dark:text-slate-400 mb-1">
            {session.browser} on {session.os}
          </p>

          <p className="text-sm text-gray-500 dark:text-slate-400">
            <span className="inline-flex items-center gap-1">
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              {session.location || 'Unknown location'}
            </span>
          </p>

          <p className="text-xs text-gray-400 dark:text-slate-500 mt-2">
            Last active: {formatRelativeTime(session.lastActivityAt)}
          </p>
        </div>

        {/* Terminate Button */}
        {!session.isCurrent && (
          <button
            type="button"
            onClick={() => onTerminate(session.id, session.deviceName)}
            className="flex-shrink-0 px-3 py-1.5 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
          >
            Logout
          </button>
        )}
      </div>
    </div>
  );
}

export function SessionsPage() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [pageState, setPageState] = useState<PageState>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    isOpen: false,
    type: 'single',
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchSessions = useCallback(async () => {
    try {
      setPageState('loading');
      const response = await getActiveSessions();
      setSessions(response.sessions);
      setPageState('loaded');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to load sessions');
      setPageState('error');
    }
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleTerminateClick = (sessionId: string, sessionName: string) => {
    setConfirmDialog({
      isOpen: true,
      type: 'single',
      sessionId,
      sessionName,
    });
  };

  const handleLogoutAllClick = () => {
    setConfirmDialog({
      isOpen: true,
      type: 'all',
    });
  };

  const handleConfirm = async () => {
    setIsProcessing(true);
    setSuccessMessage('');

    try {
      if (confirmDialog.type === 'single' && confirmDialog.sessionId) {
        await terminateSession(confirmDialog.sessionId);
        setSuccessMessage('Session terminated successfully');
      } else if (confirmDialog.type === 'all') {
        const result = await logoutAllOtherDevices();
        setSuccessMessage(`${result.sessionsTerminated} session(s) terminated successfully`);
      }

      setConfirmDialog({ isOpen: false, type: 'single' });
      await fetchSessions();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to terminate session(s)');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    setConfirmDialog({ isOpen: false, type: 'single' });
  };

  const otherSessionsCount = sessions.filter((s) => !s.isCurrent).length;

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-slate-400">Loading sessions...</p>
        </div>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 p-4">
        <div className="max-w-md w-full p-8 bg-white dark:bg-slate-800 rounded-lg shadow text-center">
          <div className="text-red-500 text-5xl mb-4">!</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">Error Loading Sessions</h1>
          <p className="text-gray-600 dark:text-slate-400 mb-6">{errorMessage}</p>
          <div className="flex flex-col gap-3">
            <button
              onClick={fetchSessions}
              className="w-full px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Try Again
            </button>
            <Link
              to="/dashboard"
              className="w-full px-6 py-2 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700 text-center"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-sm text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-100 mb-4"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Dashboard
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Active Sessions</h1>
          <p className="text-gray-600 dark:text-slate-400 mt-1">
            Manage your active sessions across all devices. You have {sessions.length} active
            session{sessions.length !== 1 ? 's' : ''}.
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-green-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              <p className="text-green-800">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && pageState === 'loaded' && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg
                className="w-5 h-5 text-red-600 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-red-800">{errorMessage}</p>
            </div>
          </div>
        )}

        {/* Logout All Button */}
        {otherSessionsCount > 0 && (
          <div className="mb-6">
            <button
              type="button"
              onClick={handleLogoutAllClick}
              className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              Logout All Other Devices ({otherSessionsCount})
            </button>
          </div>
        )}

        {/* Sessions List */}
        <div className="space-y-4">
          {sessions.map((session) => (
            <SessionCard key={session.id} session={session} onTerminate={handleTerminateClick} />
          ))}
        </div>

        {sessions.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-slate-400">No active sessions found.</p>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.type === 'all' ? 'Logout All Other Devices?' : 'Terminate Session?'}
        message={
          confirmDialog.type === 'all'
            ? `This will immediately log out all ${otherSessionsCount} other session(s). You will remain logged in on this device.`
            : `Are you sure you want to terminate the session on "${confirmDialog.sessionName}"? This will immediately log out that device.`
        }
        confirmLabel={confirmDialog.type === 'all' ? 'Logout All' : 'Terminate Session'}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isLoading={isProcessing}
      />
    </div>
  );
}
